import { Card, Inline, LinkButton, Stack, Text, textVariants } from '@etg/wings';
import { useDirection, useProperty, useTranslation } from '@eti/providers';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CABIN_BAGGAGE, MEAL, productIds, SEAT_MAP } from '@eti/products';
import { CaretDown } from '@phosphor-icons/react';
import { css } from '@eti/styles';
import { bulletStyles } from '../../../../../utils/styleUtils';
import CaretButtonWithDrawer from '../../common/CaretButtonWithDrawer';
import TitledCard from '../../../../../common/components/TitledCard';
import BaggageDrawer from '../baggages/BaggageDrawer';
import type { BoundBaggageInfoType } from '../baggages/models';
import { SHOW_BAGGAGE_QUERY_PARAMETER } from '../../../constants/general';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import type { IncludedProductType } from './utils';
import { getIncludedProducts } from './utils';
import SeatDrawer from './SeatDrawer';
import MealDrawer from './MealDrawer';
import ProductIcon from './ProductIcon';

const showMoreIconStyles = (isOpen: boolean) => css`
  margin-inline-end: 8px;
  transform: ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 150ms ease;
  vertical-align: text-bottom;
`;

export interface ProductsSectionProps {
  baggagePerBound: BoundBaggageInfoType[];
}

const ProductsSection = ({ baggagePerBound }: ProductsSectionProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { isRTL } = useDirection();
  const { addOnOrders, ancillaryBundles, extraProducts } = useOrderDetails();
  const [isShowMoreSelected, setIsShowMoreSelected] = useState(false);
  const firstButtonRef = useRef<HTMLButtonElement | null>(null);
  const flightProductsCodes = p('Overview.Products.Section.Flight.Products') || [];

  useEffect(() => {
    if (isShowMoreSelected && firstButtonRef.current) {
      firstButtonRef.current.focus();
    }
  }, [isShowMoreSelected]);

  const includedProducts = useMemo(
    () => getIncludedProducts(addOnOrders, ancillaryBundles, extraProducts),
    [addOnOrders, ancillaryBundles, extraProducts],
  );

  const { serviceProducts, flightProducts } = includedProducts.reduce<{
    serviceProducts: IncludedProductType[];
    flightProducts: IncludedProductType[];
  }>(
    (acc, product) => {
      if (flightProductsCodes.includes(product.code)) {
        acc.flightProducts.push(product);
      } else {
        acc.serviceProducts.push(product);
      }
      return acc;
    },
    { serviceProducts: [], flightProducts: [] },
  );

  const moreServiceProducts = serviceProducts.slice(1);

  const getContentByProductCode = (product: IncludedProductType) => {
    switch (product.id) {
      case MEAL.id.toString():
        return <MealDrawer product={product} />;
      case SEAT_MAP.id.toString():
        return <SeatDrawer product={product} />;
      default:
        return (
          <Card>
            <Text asHtml className={bulletStyles()}>
              {product.description}
            </Text>
          </Card>
        );
    }
  };

  return (
    <TitledCard data-testid="products-section" title={t('OrderDetails.Products.Header')}>
      <Stack>
        <Text as="h3" variant={textVariants._16}>
          {t('Overview.Products.Section.Flight.Products.Title')}
        </Text>
        <Stack data-testid="flight-products-section" role="list" spacing={8}>
          <div role="listitem">
            <CaretButtonWithDrawer
              dataTestId="baggage-button"
              drawerContent={<BaggageDrawer baggagePerBound={baggagePerBound} />}
              drawerTitle={t('OrderDetails.Baggage.Button.Title')}
              queryParam={SHOW_BAGGAGE_QUERY_PARAMETER}
              snowplow={{
                category: 'Overview',
                label: 'INFO_BAGGAGE',
              }}
            >
              <Inline alignY="center" noWrap spacing={8}>
                <ProductIcon isRTL={isRTL} productName={productIds[Number(CABIN_BAGGAGE.id)]} />
                <Stack align="start" spacing={4}>
                  <Text variant={textVariants._16_BOLD}>
                    {t('OrderDetails.Baggage.Button.Title')}
                  </Text>
                  <Text>{t('OrderDetails.Baggage.Button.Description')}</Text>
                </Stack>
              </Inline>
            </CaretButtonWithDrawer>
          </div>
          {flightProducts.map((product: IncludedProductType) => {
            return (
              <div key={product.id} role="listitem">
                <CaretButtonWithDrawer
                  dataTestId={`product-${product.id}-button`}
                  drawerContent={getContentByProductCode(product)}
                  drawerTitle={product.name}
                  key={product.id}
                  snowplow={{
                    category: 'Overview',
                    label: `INFO_${product.code}`,
                  }}
                >
                  <Inline alignY="center" noWrap spacing={8}>
                    <ProductIcon isRTL={isRTL} productName={productIds[Number(product.id)]} />
                    <Stack align="start" spacing={4}>
                      <Text variant={textVariants._16_BOLD}>{product.name}</Text>
                      <Text>
                        {t('Overview.Products.Section.Flight.Products.Text', product.name)}
                      </Text>
                    </Stack>
                  </Inline>
                </CaretButtonWithDrawer>
              </div>
            );
          })}
        </Stack>
        {serviceProducts.length > 0 && (
          <>
            <Text as="h3" variant={textVariants._16}>
              {t('Overview.Products.Section.Service.Products.Title')}
            </Text>
            <Stack data-testid="service-products-section" role="list" spacing={8}>
              <div key={serviceProducts[0].id} role="listitem">
                <CaretButtonWithDrawer
                  dataTestId={`${serviceProducts[0].id}-button`}
                  drawerContent={getContentByProductCode(serviceProducts[0])}
                  drawerTitle={serviceProducts[0].name}
                  snowplow={{
                    category: 'Overview',
                    label: `INFO_${serviceProducts[0].code}`,
                  }}
                >
                  <Inline alignY="center" noWrap spacing={8}>
                    <ProductIcon
                      isRTL={isRTL}
                      productName={productIds[Number(serviceProducts[0].id)]}
                    />
                    <Stack align="start" spacing={4}>
                      <Text variant={textVariants._16_BOLD}>{serviceProducts[0].name}</Text>
                    </Stack>
                  </Inline>
                </CaretButtonWithDrawer>
              </div>
              {isShowMoreSelected &&
                moreServiceProducts.map((product: IncludedProductType, index) => {
                  return (
                    <div key={product.id} role="listitem">
                      <CaretButtonWithDrawer
                        dataTestId={`${product.id}-button`}
                        drawerContent={getContentByProductCode(product)}
                        drawerTitle={product.name}
                        ref={index === 0 ? firstButtonRef : undefined}
                        snowplow={{
                          category: 'Overview',
                          label: `INFO_${product.code}`,
                        }}
                      >
                        <Inline alignY="center" noWrap spacing={8}>
                          <ProductIcon isRTL={isRTL} productName={productIds[Number(product.id)]} />
                          <Stack align="start" spacing={4}>
                            <Text variant={textVariants._16_BOLD}>{product.name}</Text>
                          </Stack>
                        </Inline>
                      </CaretButtonWithDrawer>
                    </div>
                  );
                })}
            </Stack>
            {moreServiceProducts.length > 0 && (
              <LinkButton
                data-testid="products-overview-show-all-button"
                onClick={() => setIsShowMoreSelected((prev) => !prev)}
              >
                <Inline align="center" spacing={8}>
                  {isShowMoreSelected
                    ? t('OrderDetails.WhatIsIncluded.Included.Collapse.Text')
                    : t('OrderDetails.WhatIsIncluded.Included.Expand.Text')}
                  <CaretDown className={showMoreIconStyles(isShowMoreSelected)} size={20} />
                </Inline>
              </LinkButton>
            )}
          </>
        )}
      </Stack>
    </TitledCard>
  );
};

export default ProductsSection;
